import type {
  PartyRecapPayload,
  WrappedSlide,
  WrappedSlideCopy,
} from "./types";
import type { PersonalHighlightContext } from "./utils";
import { calculateAverageAccuracy, calculateRankPercentile } from "./utils";

const EMPTY_COPY: WrappedSlideCopy = {
  headline: "",
  body: "",
};

export interface BuildWrappedSlidesInput {
  context: PersonalHighlightContext;
  recap: Pick<
    PartyRecapPayload,
    | "partyName"
    | "awardShowName"
    | "awardShowYear"
    | "participantCount"
    | "leaderboard"
  >;
  posterUrls?: string[];
}

export function buildWrappedSlides({
  context,
  recap,
  posterUrls = [],
}: BuildWrappedSlidesInput): WrappedSlide[] {
  const accuracyPct = Math.round(context.accuracy * 100);
  const groupAverageAccuracy = calculateAverageAccuracy(recap.leaderboard);
  const percentile = calculateRankPercentile(
    context.rank,
    recap.participantCount,
  );
  const accuracyDelta = accuracyPct - groupAverageAccuracy;

  return [
    {
      kind: "title",
      data: {
        userName: context.userName,
        partyName: recap.partyName,
        awardShowName: recap.awardShowName,
        awardShowYear: recap.awardShowYear,
        posterUrls,
      },
      aiCopy: EMPTY_COPY,
    },
    {
      kind: "recap_card",
      data: {
        rank: context.rank,
        participantCount: recap.participantCount,
        correctCount: context.correctCount,
        totalCategories: context.totalCategories,
        accuracy: accuracyPct,
        percentile,
        groupAverageAccuracy,
        accuracyDelta,
        bestFieldName: context.bestFieldName,
        bestFieldCorrectCount: context.bestFieldCorrectCount,
        exclusiveCallCount: context.exclusiveHitCategoryNames.length,
        badges: buildSummaryBadges(context, recap.participantCount),
      },
      aiCopy: EMPTY_COPY,
    },
  ];
}

function buildSummaryBadges(
  context: PersonalHighlightContext,
  participantCount: number,
): string[] {
  const badges: string[] = [];

  if (context.isTopPerformer) {
    badges.push("Party winner");
  } else if (context.rank <= Math.max(1, Math.ceil(participantCount / 2))) {
    badges.push("Top half finish");
  } else if (context.isBottomPerformer) {
    badges.push("Stayed in the mix");
  }

  if (context.bestFieldName) {
    badges.push("Category specialist");
  }

  if (context.exclusiveHitCategoryNames.length > 0) {
    badges.push("Only-you calls");
  }

  if (context.isSwingPickPlayer) {
    badges.push("Big swing pick");
  }

  if (context.comebackData) {
    badges.push("Comeback story");
  }

  if (badges.length === 0) {
    badges.push("Made the board");
  }

  return badges;
}
